import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Download, FileText, TrendingUp, Users, Calendar, BarChart3 } from 'lucide-react';
import { FiFilter, FiDownload, FiCalendar, FiUsers, FiClock, FiTrendingUp, FiCheckCircle, FiBell } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from 'recharts';
import apiClient, { endpoints } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const Reports = () => {
  const pdfRef = useRef();
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [summaryData, setSummaryData] = useState([]);
  const [expiringData, setExpiringData] = useState([]);
  const [renewalTrendData, setRenewalTrendData] = useState([]);
  const [reminderStatsData, setReminderStatsData] = useState([]);
  const [certificateTypesData, setCertificateTypesData] = useState([]);
  const [dateRange, setDateRange] = useState('6months');
  const [certificateTypeFilter, setCertificateTypeFilter] = useState('all');
  const [error, setError] = useState('');
  
  const { showError, showSuccess } = useToast();
  const { isSuperAdmin } = useAuth();

  // Chart colors
  const chartColors = {
    primary: '#FF0404',
    secondary: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3',
    light: '#e3f2fd'
  };

  const PIE_COLORS = [chartColors.primary, chartColors.secondary, chartColors.warning, chartColors.info];

  const fetchReportsData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch summary data
      const summaryResponse = await apiClient.get(endpoints.dashboard.summary, {
        params: { dateRange, certificateType: certificateTypeFilter }
      });

      // Fetch expiring certificates data
      const expiringResponse = await apiClient.get(endpoints.dashboard.expiring, {
        params: { dateRange, certificateType: certificateTypeFilter }
      });

      // Fetch renewal trend data
      const renewalResponse = await apiClient.get(endpoints.dashboard.renewalTrend, {
        params: { dateRange, certificateType: certificateTypeFilter }
      });

      // Fetch reminder stats data
      const reminderResponse = await apiClient.get(endpoints.dashboard.reminderStats, {
        params: { dateRange, certificateType: certificateTypeFilter }
      });

      // Fetch certificate types data
      const typesResponse = await apiClient.get(endpoints.dashboard.certificateTypes, {
        params: { dateRange, certificateType: certificateTypeFilter }
      });

      setSummaryData([
        {
          name: 'Total Active Certificates',
          value: summaryResponse.data.data.totalCertificates,
          icon: FiUsers,
          color: 'bg-blue-500',
          bgColor: 'bg-blue-50',
          textColor: 'text-blue-600'
        },
        {
          name: 'Expiring in Next 30 Days',
          value: summaryResponse.data.data.expiringIn30Days,
          icon: FiClock,
          color: 'bg-yellow-500',
          bgColor: 'bg-yellow-50',
          textColor: 'text-yellow-600'
        },
        {
          name: 'Renewal Rate',
          value: `${summaryResponse.data.data.renewalRate}%`,
          icon: FiTrendingUp,
          color: 'bg-green-500',
          bgColor: 'bg-green-50',
          textColor: 'text-green-600'
        },
        {
          name: 'Reminder Success Rate',
          value: `${summaryResponse.data.data.reminderSuccessRate}%`,
          icon: FiCheckCircle,
          color: 'bg-primary-500',
          bgColor: 'bg-red-50',
          textColor: 'text-brand-primary'
        }
      ]);
      setExpiringData(expiringResponse.data);
      setRenewalTrendData(renewalResponse.data);
      setReminderStatsData(reminderResponse.data);
      setCertificateTypesData(typesResponse.data);
    } catch (error) {
      console.error('Error fetching reports data:', error);
      setError('Failed to load reports data. Please try again.');
      showError('Failed to load reports data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportsData();
  }, [dateRange, certificateTypeFilter]);

  const handleExport = async (format) => {
    try {
      setExportLoading(true);
      const element = pdfRef.current;

      // Convert to canvas
      const canvas = await html2canvas(element, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");

      // Calculate dimensions
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save("download.pdf");
      setExportLoading(false);
      // const response = await apiClient.get(endpoints.dashboard.export, {
      //   params: { format, dateRange, certificateType: certificateTypeFilter },
      //   responseType: 'blob'
      // });

      // const url = window.URL.createObjectURL(new Blob([response.data]));
      // const link = document.createElement('a');
      // link.href = url;
      // link.setAttribute('download', `reports-${new Date().toISOString().split('T')[0]}.${format}`);
      // document.body.appendChild(link);
      // link.click();
      // link.remove();
      // window.URL.revokeObjectURL(url);

      // showSuccess(`Reports exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting reports:', error);
      showError('Failed to export reports');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary btn-pulse"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
            <p className="mt-1 text-sm text-gray-600">
              Comprehensive insights into certificate management and renewal performance
            </p>
          </div>
          {isSuperAdmin() && (
            <div className="flex space-x-2">
              <button
                onClick={() => exportLoading ? null : handleExport('pdf')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary btn-animate transition-all duration-200"
              >
                <FiDownload className="mr-2 h-4 w-4" />
                {exportLoading ? "Exporting..." : "Export Reports"}
              </button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-5">
        <div className="flex">
          <FiBell className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Reports
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                Generate detailed reports on certificates, reminders, and system usage. Filter data by date, status, or client.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div ref={pdfRef}>
        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Date Range Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FiCalendar className="inline mr-2 h-4 w-4" />
                  Date Range
                </label>
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
                >
                  <option value="1month">Last Month</option>
                  <option value="3months">Last 3 Months</option>
                  <option value="6months">Last 6 Months</option>
                  <option value="1year">Last Year</option>
                </select>
              </div>

              {/* Certificate Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FiFilter className="inline mr-2 h-4 w-4" />
                  Certificate Type
                </label>
                <select
                  value={certificateTypeFilter}
                  onChange={(e) => setCertificateTypeFilter(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
                >
                  <option value="all">All Types</option>
                  <option value="HSI">HSI</option>
                  <option value="AHA">AHA</option>
                  <option value="Red Cross">Red Cross</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          {summaryData.map((card) => {
            const Icon = card.icon;
            return (
              <div key={card.name} className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className={`${card.bgColor} p-3 rounded-lg`}>
                        <Icon className={`h-6 w-6 ${card.textColor}`} />
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">
                          {card.name}
                        </dt>
                        <dd className="text-lg font-medium text-gray-900">
                          {typeof card.value === 'number' ? card.value.toLocaleString() : card.value}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Expiring Certificates Chart */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Certificates Expiring by Timeframe</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={expiringData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="timeframe" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill={chartColors.primary} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Renewal Rate Over Time Chart */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Renewal Rate Over Time</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={renewalTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="renewals" stroke={chartColors.success} strokeWidth={2} />
                <Line type="monotone" dataKey="expirations" stroke={chartColors.primary} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Reminder Delivery Stats Chart */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Reminder Delivery Reports</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={reminderStatsData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {reminderStatsData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Certificates by Type Chart */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Assigned Certificates by Type</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={certificateTypesData?.map((item) => { return { name: item.type, value: item.count } })}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {certificateTypesData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;